(function () {
  'use strict';

  var CONFIG = Object.freeze({
    catalogCacheKey: 'ndCatalogSearch:catalog:v3',
    collectionIntentCacheKey: 'ndCatalogSearch:collection-intents:v1',
    resolutionCachePrefix: 'ndCatalogSearch:resolution:v4:',
    catalogCacheTtlMs: 5 * 60 * 1000,
    semanticFallbackMaxResults: 24,
    storefrontEndpoint: 'https://nappadori-di.myshopify.com/api/2026-07/graphql.json',
    // This is the existing public Storefront token. It cannot access the Admin API.
    storefrontAccessToken: 'fe306b30e3241d326f2d9de1c0573ce7'
  });

  var CATALOG_QUERY = [
    'query CatalogSearchCatalog($cursor: String) {',
    '  products(first: 250, after: $cursor, sortKey: ID) {',
    '    nodes {',
    '      handle',
    '      title',
    '      productType',
    '      options { name values }',
    '    }',
    '    pageInfo { hasNextPage endCursor }',
    '  }',
    '}'
  ].join('\n');

  var COLLECTION_PRODUCTS_QUERY = [
    'query CatalogSearchCollection($handle: String!, $cursor: String) {',
    '  collection(handle: $handle) {',
    '    products(first: 250, after: $cursor, sortKey: ID) {',
    '      nodes { handle }',
    '      pageInfo { hasNextPage endCursor }',
    '    }',
    '  }',
    '}'
  ].join('\n');

  var CONTROLLED_TOKEN_MAP = Object.freeze({
    organizer: 'organiser',
    trouser: 'trouser',
    pant: 'trouser',
    trolley: 'luggage',
    suitcase: 'luggage'
  });

  // Only these customer phrases opt into collection membership. Every other collection remains unsearchable.
  var COLLECTION_INTENTS = Object.freeze([
    { key: 'handbags', tokens: ['handbag'], collectionHandles: ['handbags'] },
    { key: 'gifts', tokens: ['gift'], collectionHandles: ['gifts'] },
    { key: 'mens-accessories', tokens: ['men', 'accessory'], collectionHandles: ['men', 'accessories'] },
    { key: 'womens-accessories', tokens: ['women', 'accessory'], collectionHandles: ['women', 'accessories'] }
  ]);

  var state = {
    mode: '',
    rawQuery: '',
    backendQuery: ''
  };
  var catalogPromise = null;
  var collectionIntentPromise = null;

  function normalizeText(value) {
    var normalized = String(value || '').trim();

    if (normalized.normalize) {
      normalized = normalized.normalize('NFKD');
    }

    return normalized
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/['’]s\b/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function normalizePluralWord(word) {
    if (word.length <= 3) {
      return word;
    }

    // Only handle regular English plural forms. This deliberately avoids blindly removing a final "s".
    if (/ies$/.test(word) && word.length > 4) {
      return word.slice(0, -3) + 'y';
    }

    if (/zzes$/.test(word)) {
      return word.slice(0, -3);
    }

    if (/(?:ches|shes|xes|zes|sses)$/.test(word)) {
      return word.slice(0, -2);
    }

    if (/s$/.test(word) && !/(?:ss|us|is)$/.test(word)) {
      return word.slice(0, -1);
    }

    return word;
  }

  function canonicalToken(word) {
    var normalized = normalizePluralWord(normalizeText(word));
    return CONTROLLED_TOKEN_MAP[normalized] || normalized;
  }

  function tokenize(value) {
    var normalized = normalizeText(value);

    if (!normalized) {
      return [];
    }

    return normalized.split(' ').map(canonicalToken).filter(Boolean);
  }

  function uniqueTokens(tokens) {
    var seen = Object.create(null);

    return tokens.filter(function (token) {
      if (seen[token]) {
        return false;
      }

      seen[token] = true;
      return true;
    });
  }

  function catalogCacheKey() {
    return CONFIG.catalogCacheKey;
  }

  function collectionIntentCacheKey() {
    return CONFIG.collectionIntentCacheKey;
  }

  function resolutionCacheKey(rawQuery) {
    // Keep the route/display text tied to the exact submitted query. Canonical tokens
    // intentionally share results, but must not turn a search for "bags" into "bag".
    return CONFIG.resolutionCachePrefix + encodeURIComponent(String(rawQuery || '').trim());
  }

  function getSessionValue(key) {
    try {
      var cached = JSON.parse(window.sessionStorage.getItem(key) || 'null');
      return cached && cached.expiresAt > Date.now() ? cached.value : null;
    } catch (error) {
      return null;
    }
  }

  function setSessionValue(key, value) {
    try {
      window.sessionStorage.setItem(key, JSON.stringify({
        expiresAt: Date.now() + CONFIG.catalogCacheTtlMs,
        value: value
      }));
    } catch (error) {}
  }

  function storefrontRequest(query, variables) {
    return fetch(CONFIG.storefrontEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': CONFIG.storefrontAccessToken
      },
      body: JSON.stringify({ query: query, variables: variables })
    }).then(function (response) {
      if (!response.ok) {
        throw new Error('Catalog request failed: ' + response.status);
      }

      return response.json();
    }).then(function (payload) {
      if (payload.errors || !payload.data) {
        throw new Error('Catalog response was invalid.');
      }

      return payload.data;
    });
  }

  function indexColorOptions(options) {
    var colorOptionValues = [];

    (options || []).forEach(function (option) {
      var optionName = normalizeText(option.name);

      if (optionName !== 'color' && optionName !== 'colour') {
        return;
      }

      (option.values || []).forEach(function (value) {
        var tokens = uniqueTokens(tokenize(value));

        if (tokens.length) {
          colorOptionValues.push(tokens);
        }
      });
    });

    return colorOptionValues;
  }

  function indexProduct(product) {
    var titleTokens = tokenize(product.title);
    var productTypeTokens = tokenize(product.productType);
    var tokenSet = Object.create(null);

    titleTokens.concat(productTypeTokens).forEach(function (token) {
      tokenSet[token] = true;
    });

    return {
      handle: product.handle,
      titleTokens: titleTokens,
      productTypeTokens: productTypeTokens,
      colorOptionValues: indexColorOptions(product.options),
      tokenSet: tokenSet
    };
  }

  function loadCollectionProductHandles(handle) {
    var handles = [];

    function loadPage(cursor) {
      return storefrontRequest(COLLECTION_PRODUCTS_QUERY, { handle: handle, cursor: cursor }).then(function (data) {
        var collection = data.collection;

        if (!collection || !collection.products) {
          throw new Error('Collection intent source was unavailable: ' + handle);
        }

        handles = handles.concat((collection.products.nodes || []).map(function (product) {
          return product.handle;
        }));

        if (collection.products.pageInfo && collection.products.pageInfo.hasNextPage) {
          return loadPage(collection.products.pageInfo.endCursor);
        }

        return { handle: handle, productHandles: handles };
      });
    }

    return loadPage(null);
  }

  function loadCollectionIntentMembers(products) {
    var collectionHandles = Object.create(null);
    var visibleProducts = Object.create(null);
    var cached = getSessionValue(collectionIntentCacheKey());

    if (cached && typeof cached === 'object') {
      return Promise.resolve(cached);
    }

    if (collectionIntentPromise) {
      return collectionIntentPromise;
    }

    products.forEach(function (product) {
      visibleProducts[product.handle] = true;
    });

    COLLECTION_INTENTS.forEach(function (intent) {
      intent.collectionHandles.forEach(function (handle) {
        collectionHandles[handle] = true;
      });
    });

    collectionIntentPromise = Promise.all(Object.keys(collectionHandles).map(loadCollectionProductHandles)).then(function (collections) {
      var membersByCollection = Object.create(null);
      var membersByIntent = Object.create(null);

      collections.forEach(function (collection) {
        membersByCollection[collection.handle] = Object.create(null);
        collection.productHandles.forEach(function (handle) {
          membersByCollection[collection.handle][handle] = true;
        });
      });

      COLLECTION_INTENTS.forEach(function (intent) {
        var firstCollection = membersByCollection[intent.collectionHandles[0]] || Object.create(null);
        var members = Object.create(null);

        Object.keys(firstCollection).forEach(function (handle) {
          var isVisible = Boolean(visibleProducts[handle]);
          var belongsToEveryCollection = intent.collectionHandles.every(function (collectionHandle) {
            return Boolean(membersByCollection[collectionHandle] && membersByCollection[collectionHandle][handle]);
          });

          if (isVisible && belongsToEveryCollection) {
            members[handle] = true;
          }
        });

        membersByIntent[intent.key] = members;
      });

      setSessionValue(collectionIntentCacheKey(), membersByIntent);
      return membersByIntent;
    }).then(function (membersByIntent) {
      collectionIntentPromise = null;
      return membersByIntent;
    }).catch(function (error) {
      collectionIntentPromise = null;
      throw error;
    });

    return collectionIntentPromise;
  }

  function loadCatalog() {
    var cached = getSessionValue(catalogCacheKey());

    if (cached && Array.isArray(cached.products)) {
      return Promise.resolve(cached);
    }

    if (catalogPromise) {
      return catalogPromise;
    }

    var products = [];

    function loadPage(cursor) {
      return storefrontRequest(CATALOG_QUERY, { cursor: cursor }).then(function (data) {
        var connection = data.products;

        if (!connection) {
          throw new Error('Catalog connection was invalid.');
        }

        products = products.concat((connection.nodes || []).map(indexProduct));

        if (connection.pageInfo && connection.pageInfo.hasNextPage) {
          return loadPage(connection.pageInfo.endCursor);
        }

        var lexicon = buildLexicon(products);
          var colorValueTokens = buildColorValueTokens(products);
          var colorLexicon = buildColorLexicon(colorValueTokens);
        var queryLexicon = buildQueryLexicon(lexicon, colorLexicon);
        var catalog = {
            products: products,
            lexicon: lexicon,
            colorLexicon: colorLexicon,
            colorValueTokens: colorValueTokens,
          queryLexicon: queryLexicon
        };

        setSessionValue(catalogCacheKey(), catalog);
        return catalog;
      });
    }

    catalogPromise = loadPage(null).then(function (catalog) {
      catalogPromise = null;
      return catalog;
    }).catch(function (error) {
      catalogPromise = null;
      throw error;
    });

    return catalogPromise;
  }

  function buildLexicon(products) {
    var lexicon = Object.create(null);

    products.forEach(function (product) {
      Object.keys(product.tokenSet).forEach(function (token) {
        lexicon[token] = true;
      });
    });

    // The luggage group is a controlled classification, even where the catalog uses suitcase/carry-on terms.
    lexicon.luggage = true;
    return Object.keys(lexicon);
  }

  function buildColorValueTokens(products) {
    var values = Object.create(null);

    products.forEach(function (product) {
      product.colorOptionValues.forEach(function (tokens) {
        values[tokens.join('\u0001')] = tokens;
      });
    });

    return Object.keys(values).map(function (key) {
      return values[key];
    });
  }

  function buildColorLexicon(colorValueTokens) {
    var colorLexicon = Object.create(null);

    colorValueTokens.forEach(function (tokens) {
      tokens.forEach(function (token) {
          colorLexicon[token] = true;
      });
    });

    return Object.keys(colorLexicon);
  }

  function buildQueryLexicon(lexicon, colorLexicon) {
    var queryLexicon = Object.create(null);

    lexicon.concat(colorLexicon).forEach(function (token) {
      queryLexicon[token] = true;
    });

    COLLECTION_INTENTS.forEach(function (intent) {
      intent.tokens.forEach(function (token) {
        queryLexicon[token] = true;
      });
    });

    return Object.keys(queryLexicon);
  }

  function editDistance(left, right) {
    var beforePrevious = null;
    var previous = [];
    var current = [];
    var row;
    var column;

    for (column = 0; column <= right.length; column += 1) {
      previous[column] = column;
    }

    for (row = 1; row <= left.length; row += 1) {
      current[0] = row;

      for (column = 1; column <= right.length; column += 1) {
        current[column] = Math.min(
          previous[column] + 1,
          current[column - 1] + 1,
          previous[column - 1] + (left.charAt(row - 1) === right.charAt(column - 1) ? 0 : 1)
        );

        // A single swapped adjacent pair (candel/candle) is one typing error.
        if (row > 1 && column > 1 &&
            left.charAt(row - 1) === right.charAt(column - 2) &&
            left.charAt(row - 2) === right.charAt(column - 1)) {
          current[column] = Math.min(current[column], beforePrevious[column - 2] + 1);
        }
      }

      beforePrevious = previous;
      previous = current;
      current = [];
    }

    return previous[right.length];
  }

  function correctedToken(token, lexicon) {
    if (lexicon.indexOf(token) !== -1 || token.length < 4) {
      return token;
    }

    var maximumDistance = token.length >= 7 ? 2 : 1;
    var bestToken = '';
    var bestDistance = maximumDistance + 1;
    var ambiguous = false;

    lexicon.forEach(function (candidate) {
      if (candidate.length < 4 || Math.abs(candidate.length - token.length) > maximumDistance) {
        return;
      }

      if (token.slice(0, 2) !== candidate.slice(0, 2)) {
        return;
      }

      var distance = editDistance(token, candidate);

      if (distance > maximumDistance || distance / Math.max(token.length, candidate.length) > 0.25) {
        return;
      }

      if (distance > 1 && token.slice(0, 3) !== candidate.slice(0, 3)) {
        return;
      }

      if (distance < bestDistance) {
        bestToken = candidate;
        bestDistance = distance;
        ambiguous = false;
      } else if (distance === bestDistance && candidate !== bestToken) {
        ambiguous = true;
      }
    });

    return !ambiguous && bestDistance <= maximumDistance ? bestToken : token;
  }

  function resolveTokens(rawQuery, lexicon, allowCorrection) {
    var queryTokens = uniqueTokens(tokenize(rawQuery));
    var correctedTokens = queryTokens.map(function (token) {
      return allowCorrection ? correctedToken(token, lexicon) : token;
    });
    var knownTokens = Object.create(null);

    lexicon.forEach(function (token) {
      knownTokens[token] = true;
    });

    return {
      tokens: correctedTokens,
      isReliable: correctedTokens.length > 0 && correctedTokens.every(function (token) {
        return Boolean(knownTokens[token]);
      })
    };
  }

  function isLuggageClassification(product) {
    var isTravelEquipment = product.productTypeTokens.indexOf('travel') !== -1 && product.productTypeTokens.indexOf('equipment') !== -1;
    var isTravelKit = product.titleTokens.indexOf('kit') !== -1;

    // Travel kits share this type but are not luggage; classify using title/type only.
    return isTravelEquipment && !isTravelKit;
  }

  function findCollectionIntents(queryTokens) {
    var consumed = [];
    var intents = [];

    queryTokens.forEach(function () {
      consumed.push(false);
    });

    COLLECTION_INTENTS.slice().sort(function (left, right) {
      return right.tokens.length - left.tokens.length;
    }).forEach(function (intent) {
      var index;

      for (index = 0; index <= queryTokens.length - intent.tokens.length; index += 1) {
        var matchesPhrase = intent.tokens.every(function (token, offset) {
          return !consumed[index + offset] && queryTokens[index + offset] === token;
        });

        if (!matchesPhrase) {
          continue;
        }

        intent.tokens.forEach(function (token, offset) {
          consumed[index + offset] = true;
        });
        intents.push(intent.key);
        break;
      }
    });

    return {
      intentKeys: intents,
      remainingTokens: queryTokens.filter(function (token, index) {
        return !consumed[index];
      })
    };
  }

  function findColorIntents(queryTokens, colorValueTokens) {
    var consumed = [];
    var colorTokenLists = [];

    queryTokens.forEach(function () {
      consumed.push(false);
    });

    colorValueTokens.slice().sort(function (left, right) {
      return right.length - left.length;
    }).forEach(function (colorTokens) {
      var index;

      for (index = 0; index <= queryTokens.length - colorTokens.length; index += 1) {
        var matchesValue = colorTokens.every(function (token, offset) {
          return !consumed[index + offset] && queryTokens[index + offset] === token;
        });

        if (!matchesValue) {
          continue;
        }

        colorTokens.forEach(function (token, offset) {
          consumed[index + offset] = true;
        });
        colorTokenLists.push(colorTokens);
        break;
      }
    });

    return {
      colorTokenLists: colorTokenLists,
      remainingTokens: queryTokens.filter(function (token, index) {
        return !consumed[index];
      })
    };
  }

  function buildSearchConstraints(queryTokens, catalog) {
    var intentResolution = findCollectionIntents(queryTokens);
    var colorResolution = findColorIntents(intentResolution.remainingTokens, catalog.colorValueTokens);
    var textTokens = colorResolution.remainingTokens;

    return {
      colorTokenLists: colorResolution.colorTokenLists,
      intentKeys: intentResolution.intentKeys,
      textTokens: textTokens,
      isReliable: textTokens.every(function (token) {
        return catalog.lexicon.indexOf(token) !== -1;
      })
    };
  }

  function productMatchesColors(product, colorTokenLists) {
    return colorTokenLists.every(function (colorTokens) {
      return product.colorOptionValues.some(function (optionValueTokens) {
        return optionValueTokens.length === colorTokens.length && optionValueTokens.every(function (token, index) {
          return token === colorTokens[index];
        });
      });
    });
  }

  function productMatches(product, constraints, collectionIntentMembers) {
    if (constraints.colorTokenLists.length && !productMatchesColors(product, constraints.colorTokenLists)) {
      return false;
    }

    if (!constraints.intentKeys.every(function (intentKey) {
      return Boolean(collectionIntentMembers[intentKey] && collectionIntentMembers[intentKey][product.handle]);
    })) {
      return false;
    }

    return constraints.textTokens.every(function (token) {
      if (token === 'luggage') {
        return isLuggageClassification(product);
      }

      return Boolean(product.tokenSet[token]);
    });
  }

  function resolveControlledHandles(catalog, constraints) {
    var membersPromise = constraints.intentKeys.length ? loadCollectionIntentMembers(catalog.products) : Promise.resolve(Object.create(null));

    return membersPromise.then(function (collectionIntentMembers) {
      return catalog.products.filter(function (product) {
        return productMatches(product, constraints, collectionIntentMembers);
      }).map(function (product) {
        return product.handle;
      });
    });
  }

  function buildHandleQuery(handles) {
    return handles.map(function (handle) {
      return 'handle:' + handle;
    }).join(' OR ');
  }

  function controlledResolution(rawQuery, handles) {
    return {
      mode: 'controlled',
      rawQuery: rawQuery,
      backendQuery: buildHandleQuery(handles),
      handles: handles
    };
  }

  function emptyResolution(rawQuery) {
    return {
      mode: 'empty',
      rawQuery: rawQuery,
      backendQuery: 'handle:__nappadori_catalog_search_no_match__',
      handles: []
    };
  }

  function semanticFallbackResolution(rawQuery) {
    return {
      mode: 'fallback',
      rawQuery: rawQuery,
      backendQuery: rawQuery,
      handles: []
    };
  }

  function fallbackResultCount(rawQuery) {
    var target = new URL(window.routes.searchUrl, window.location.origin);
    target.searchParams.set('section_id', 'predictive-search');
    target.searchParams.set('q', rawQuery);
    target.searchParams.set('type', 'product');

    return fetch(target.toString(), {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    }).then(function (response) {
      if (!response.ok) {
        throw new Error('Fallback search request failed: ' + response.status);
      }

      return response.text();
    }).then(function (content) {
      var documentFragment = new DOMParser().parseFromString(content, 'text/html');
      var resultContainer = documentFragment.querySelector('[data-search-results-count]');
      var resultCount = resultContainer ? parseInt(resultContainer.getAttribute('data-search-results-count'), 10) : NaN;

      return Number.isFinite(resultCount) ? resultCount : Infinity;
    }).catch(function () {
      return Infinity;
    });
  }

  function getCachedResolution(rawQuery) {
    return getSessionValue(resolutionCacheKey(rawQuery));
  }

  function resolveQuery(rawQuery) {
    var cleanRawQuery = String(rawQuery || '').trim();

    if (!tokenize(cleanRawQuery).length) {
      return Promise.resolve(emptyResolution(cleanRawQuery));
    }

    var cached = getCachedResolution(cleanRawQuery);

    if (cached && cached.mode && cached.backendQuery) {
      return Promise.resolve(cached);
    }

    return loadCatalog().then(function (catalog) {
      var query = resolveTokens(cleanRawQuery, catalog.queryLexicon, false);
      var constraints = query.isReliable ? buildSearchConstraints(query.tokens, catalog) : null;
      var literalMatches = constraints && constraints.isReliable ? resolveControlledHandles(catalog, constraints) : Promise.resolve([]);

      return literalMatches.then(function (handles) {
        if (handles.length) {
          return handles;
        }

        var corrected = resolveTokens(cleanRawQuery, catalog.queryLexicon, true);
        if (!corrected.isReliable || corrected.tokens.join(' ') === query.tokens.join(' ')) {
          return [];
        }

        var correctedConstraints = buildSearchConstraints(corrected.tokens, catalog);
        return correctedConstraints.isReliable ? resolveControlledHandles(catalog, correctedConstraints) : [];
      });
    }).then(function (handles) {

      if (handles.length > 0) {
        return controlledResolution(cleanRawQuery, handles);
      }

      return fallbackResultCount(cleanRawQuery).then(function (count) {
        return count <= CONFIG.semanticFallbackMaxResults ? semanticFallbackResolution(cleanRawQuery) : emptyResolution(cleanRawQuery);
      });
    }).catch(function () {
      // Without a complete classification catalog, do not expose a large semantic result set.
      return emptyResolution(cleanRawQuery);
    }).then(function (resolution) {
      setSessionValue(resolutionCacheKey(cleanRawQuery), resolution);
      return resolution;
    });
  }

  function setState(resolution) {
    state.mode = resolution.mode;
    state.rawQuery = resolution.rawQuery;
    state.backendQuery = resolution.backendQuery;
  }

  function navigationUrl(url, resolution) {
    var target = new URL(url, window.location.origin);

    target.searchParams.set('q', resolution.backendQuery);
    target.searchParams.set('type', 'product');
    target.searchParams.set('nd_mode', resolution.mode);
    target.searchParams.set('nd_q', resolution.rawQuery);
    return target.toString();
  }

  function requestUrl(url) {
    var target = new URL(url, window.location.origin);

    if (!state.mode || !state.rawQuery || !state.backendQuery) {
      return target.toString();
    }

    return navigationUrl(target.toString(), state);
  }

  function displayUrl(url) {
    var target = new URL(requestUrl(url));
    target.searchParams.delete('section_id');
    return target.toString();
  }

  function preparePaginationLinks() {
    if (!state.mode || !state.rawQuery || !state.backendQuery) {
      return;
    }

    document.querySelectorAll('[data-section-type="collection"] a[href]').forEach(function (link) {
      var target = new URL(link.href, window.location.origin);

      if (target.pathname !== window.routes.searchUrl || !target.searchParams.has('page')) {
        return;
      }

      link.href = displayUrl(target.toString());
    });
  }

  function preparePredictiveContent(content, resolution) {
    var documentFragment = new DOMParser().parseFromString(content, 'text/html');
    var viewAllLink = documentFragment.querySelector('.Segment__Title a[href]');

    if (viewAllLink) {
      viewAllLink.setAttribute('href', navigationUrl(viewAllLink.href, resolution));
    }

    return documentFragment.body.innerHTML;
  }

  function fetchPredictiveContent(rawQuery) {
    return resolveQuery(rawQuery).then(function (resolution) {
      setState(resolution);
      var target = new URL(window.routes.searchUrl, window.location.origin);
      target.searchParams.set('section_id', 'predictive-search');

      return fetch(navigationUrl(target.toString(), resolution), {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      }).then(function (response) {
        if (!response.ok) {
          throw new Error('Predictive search request failed: ' + response.status);
        }

        return response.text().then(function (content) {
          return preparePredictiveContent(content, resolution);
        });
      });
    });
  }

  function resolveSearchPage() {
    if (window.theme.pageType !== 'search') {
      return;
    }

    var currentUrl = new URL(window.location.href);
    var mode = currentUrl.searchParams.get('nd_mode');
    var rawQuery = currentUrl.searchParams.get('nd_q') || currentUrl.searchParams.get('q') || '';
    var backendQuery = currentUrl.searchParams.get('q') || '';

    document.querySelectorAll('input[name="q"]:not([type="hidden"])').forEach(function (input) {
      if (input.form && new URL(input.form.action, window.location.origin).pathname === window.routes.searchUrl) {
        input.value = rawQuery;
      }
    });

    if (mode && rawQuery && backendQuery) {
      setState({ mode: mode, rawQuery: rawQuery, backendQuery: backendQuery });
      preparePaginationLinks();
      return;
    }

    if (!tokenize(rawQuery).length) {
      return;
    }

    resolveQuery(rawQuery).then(function (resolution) {
      setState(resolution);

      if (resolution.mode === 'fallback' && resolution.backendQuery === backendQuery) {
        if (window.history.replaceState) {
          window.history.replaceState(window.history.state, '', navigationUrl(currentUrl.toString(), resolution));
        }

        preparePaginationLinks();
        return;
      }

      window.location.replace(navigationUrl(currentUrl.toString(), resolution));
    });
  }

  function interceptSearchSubmissions() {
    document.addEventListener('submit', function (event) {
      var form = event.target;

      if (!(form instanceof HTMLFormElement) || form.classList.contains('collection-filters-form')) {
        return;
      }

      var queryInput = form.querySelector('[name="q"]');
      var action = new URL(form.action || window.location.href, window.location.origin);

      if (!queryInput || action.pathname !== window.routes.searchUrl) {
        return;
      }

      var rawQuery = queryInput.value;

      if (!tokenize(rawQuery).length) {
        return;
      }

      event.preventDefault();
      var formData = new FormData(form);
      var target = new URL(action.toString());
      new URLSearchParams(formData).forEach(function (value, key) {
        target.searchParams.set(key, value);
      });

      resolveQuery(rawQuery).then(function (resolution) {
        window.location.assign(navigationUrl(target.toString(), resolution));
      });
    }, true);
  }

  window.NappaDoriCatalogSearch = {
    getDisplayUrl: displayUrl,
    getPredictiveContent: fetchPredictiveContent,
    getRequestUrl: requestUrl,
    resolve: resolveQuery,
    getState: function () {
      return {
        mode: state.mode,
        rawQuery: state.rawQuery,
        backendQuery: state.backendQuery
      };
    }
  };

  interceptSearchSubmissions();
  document.addEventListener('DOMContentLoaded', function () {
    resolveSearchPage();
    preparePaginationLinks();
  });
  document.addEventListener('theme:loading:end', preparePaginationLinks);
}());
